import React, { useState } from "react";
import { ConfirmDialog } from "../../components/Dialogs";

const normalizeValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isBlockedRestriction = (booking = {}) =>
  normalizeValue(
    booking.booking_details?.restriction_type ||
      booking.restriction_type ||
      booking.booking_details?.type ||
      booking.type,
  ) === "block" ||
  (
    normalizeValue(booking.user_name || booking.username || booking.userName) === "n/a" &&
    !booking.user &&
    !booking.user_id
  );

const getDisplayMeta = (booking = {}) => {
  const rawType =
    booking.bookingType ||
    booking.booking_type ||
    booking.booking_details?.type ||
    booking.type ||
    "One-Time";
  const blocked = isBlockedRestriction(booking);

  return {
    userName: blocked
      ? "Blocked"
      : booking.user_name || booking.username || booking.userName || booking.user?.name || "Unknown User",
    displayType: rawType,
    cancelType: blocked ? `Blocked ${rawType}` : rawType,
    blocked,
  };
};

const shouldShowInOccupancy = (meta = {}) => {
  const type = normalizeValue(meta.displayType);
  return meta.blocked || (type !== "one-time" && type !== "onetime");
};

const isBookingLikeRecord = (value = {}) =>
  Boolean(
    value &&
      typeof value === "object" &&
      (
        value.bookingId ||
        value.booking_id ||
        value.id ||
        value._id ||
        value.bookingType ||
        value.booking_type ||
        value.type ||
        value.booking_details
      )
  );

const getOccupancyRecords = (details = {}) => {
  if (Array.isArray(details)) return details;
  if (Array.isArray(details.bookings) && details.bookings.length > 0) return details.bookings;

  if (Array.isArray(details.userWiseBooking) && details.userWiseBooking.length > 0) {
    return details.userWiseBooking.flatMap((userObj) =>
      (userObj.bookings || []).map((booking) => ({
        ...booking,
        user_name: userObj.user_name || userObj.username || userObj.userName,
      }))
    );
  }

  if (isBookingLikeRecord(details)) return [details];
  return [];
};

const OccupancyItem = ({ userName, type, onCancel, bookingId, recurringId, permanentId, blocked = false }) => {
  const normalizedType = normalizeValue(type);
  const isPermanent = normalizedType === "permanent";
  const isRecurring = normalizedType === "recurring";
  const isDateRange = normalizedType === "date-range" || normalizedType === "date range";
  const isOneTime = normalizedType === "one-time" || normalizedType === "onetime";

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });

  // Only Permanent and Recurring get a cancel button
  const canCancel = blocked || isPermanent || isRecurring;
  // Pass the correct group-level ID to the handler
  const cancelId = isPermanent ? permanentId : isRecurring ? recurringId : bookingId;

  return (
    <>
    <div className={`flex items-center justify-between gap-4 rounded-2xl border p-4 transition-all ${
      blocked
        ? "border-rose-200 bg-rose-50/40"
        : isPermanent 
        ? "border-indigo-100 bg-indigo-50/30" 
        : isRecurring 
          ? "border-amber-100 bg-amber-50/30"
          : isDateRange
            ? "border-emerald-100 bg-emerald-50/30"
            : isOneTime
              ? "border-sky-100 bg-sky-50/30"
              : "border-slate-100 bg-slate-50/30"
    }`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">
          {userName}
        </p>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          {blocked ? (
            <>
              <span className="inline-block rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-rose-100 text-rose-600">
                Blocked
              </span>
              <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${
                isPermanent 
                  ? "bg-indigo-100 text-indigo-600" 
                  : isRecurring 
                    ? "bg-amber-100 text-amber-600"
                    : isDateRange
                      ? "bg-emerald-100 text-emerald-600"
                      : isOneTime
                        ? "bg-sky-100 text-sky-600"
                        : "bg-slate-200 text-slate-600"
              }`}>
                {type}
              </span>
            </>
          ) : (
            <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${
              isPermanent 
                ? "bg-indigo-100 text-indigo-600" 
                : isRecurring 
                  ? "bg-amber-100 text-amber-600"
                  : isDateRange
                    ? "bg-emerald-100 text-emerald-600"
                    : isOneTime
                      ? "bg-sky-100 text-sky-600"
                      : "bg-slate-200 text-slate-600"
            }`}>
              {type}
            </span>
          )}
        </div>
      </div>
        {canCancel && (
        <button
          type="button"
          onClick={() => {
            setConfirmDialog({
              isOpen: true,
              title: "Cancel Booking",
              message: `Are you sure you want to cancel this ${type} booking for ${userName}? This will cancel ALL dates in this booking.`,
              isDestructive: true,
              onConfirm: () => {
                setConfirmDialog({ isOpen: false });
                onCancel(cancelId, type);
              },
              onCancel: () => setConfirmDialog({ isOpen: false }),
            });
          }}
          className="rounded-xl border border-rose-100 bg-white px-3 py-2 text-[10px] font-bold text-rose-600 shadow-sm transition-all hover:bg-rose-50"
        >
          Cancel All
        </button>
      )}
      
      <ConfirmDialog {...confirmDialog} />
    </div>
    </>
  );
};

const SeatBookingDetails = ({ details, loading, onCancel, onViewAllClick }) => {
  if (loading) {
    return (
      <div className="mb-6 rounded-[24px] border border-slate-200 bg-white p-6 animate-pulse">
        <div className="h-4 w-24 bg-slate-100 rounded mb-4" />
        <div className="space-y-3">
          <div className="h-16 bg-slate-50 rounded-2xl" />
          <div className="h-16 bg-slate-50 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!details) return null;

  const distinctOccupancy = [];
  const occupancyMap = new Map();
  const allBookings = getOccupancyRecords(details);

  allBookings.forEach((b) => {
    const meta = getDisplayMeta(b);
    if (!shouldShowInOccupancy(meta)) return;
    const bId = b.booking_id || b.bookingId || b.id || b._id;
    const key = `${meta.userName}-${meta.displayType}`.toLowerCase();

    if (!occupancyMap.has(key)) {
      occupancyMap.set(key, {
        userName: meta.userName,
        type: meta.cancelType,
        displayType: meta.displayType,
        blocked: meta.blocked,
        bookingId: bId,
        recurringId: b.recurring_id || b.recurringId || null,
        permanentId: b.permanent_id || b.permanentId || null,
      });
    }
  });

  distinctOccupancy.push(...Array.from(occupancyMap.values()));

  const hasAnyOccupancy = distinctOccupancy.length > 0;

  return (
    <div className="mb-6  border border-slate-200 bg-white p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          Seat Occupancy Status
        </h3>
        <div className="flex items-center gap-2">
          {hasAnyOccupancy && onViewAllClick && (
            <button
              type="button"
              onClick={onViewAllClick}
              className="text-[11px] font-bold text-semibold  uppercase  underline"
            >
              View All List
            </button>
          )}
          {!hasAnyOccupancy && (
            <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">Available</span>
          )}
        </div>
      </div>

      {!hasAnyOccupancy ? (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-center">
          <p className="text-xs font-medium text-emerald-700">No current occupancy for this seat.</p>
        </div>
      ) : (
        /* Status view: Show distinct (User + Type + ID) combinations */
        <div className="space-y-3">
          {distinctOccupancy.map((occ, idx) => (
            <OccupancyItem 
              key={idx}
              userName={occ.userName}
              type={occ.displayType || occ.type}
              bookingId={occ.bookingId}
              recurringId={occ.recurringId}
              permanentId={occ.permanentId}
              blocked={occ.blocked}
              onCancel={(id) => onCancel(id, occ.type)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SeatBookingDetails;
